import { useState, useEffect } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, Tabs, Tab, Pagination } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { formatDateOnlyEsMX } from '../../utils/apiDateFormat'

const SATCatalogs = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('versions')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadVersion, setUploadVersion] = useState('4.0')
  const [uploadFechaInicio, setUploadFechaInicio] = useState('')
  const [uploadFechaFin, setUploadFechaFin] = useState('')
  const [uploadDescripcion, setUploadDescripcion] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50 // Coincide con PAGE_SIZE del backend
  
  // Estados para configuraciones por cliente
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [editingConfig, setEditingConfig] = useState(null)
  const [configClienteId, setConfigClienteId] = useState('')
  const [configVersionId, setConfigVersionId] = useState('')
  const [configFechaAplicacion, setConfigFechaAplicacion] = useState('')
  const { clientId: configFilterClientId, setClientId: setConfigFilterClientId, selectDisabled } = useSyncedLocalClientId()
  const [isSavingConfig, setIsSavingConfig] = useState(false)
  
  // Estados para crear/editar registro del catálogo
  const [showCatalogItemModal, setShowCatalogItemModal] = useState(false)
  const [editingCatalogItem, setEditingCatalogItem] = useState(null)
  const [catalogItemClave, setCatalogItemClave] = useState('')
  const [catalogItemDescripcion, setCatalogItemDescripcion] = useState('')
  const [catalogItemIncluye, setCatalogItemIncluye] = useState('')
  const [catalogItemSimilares, setCatalogItemSimilares] = useState('')
  const [catalogItemVersionId, setCatalogItemVersionId] = useState('')
  const [catalogItemActivo, setCatalogItemActivo] = useState(true)
  const [isSavingCatalogItem, setIsSavingCatalogItem] = useState(false)

  // Debounce para la búsqueda y resetear página
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setCurrentPage(1) // Resetear a página 1 cuando cambie la búsqueda
    }, 500) // Esperar 500ms después de que el usuario deje de escribir

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Obtener versiones del catálogo SAT
  const { data: versions } = useQuery(
    'sat-versions',
    async () => {
      const response = await api.get('/accounting/versiones-catalogo-sat/')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener catálogo SAT con filtro de búsqueda y paginación
  const { data: catalogData, isLoading: isLoadingCatalog } = useQuery(
    ['sat-catalog', debouncedSearchTerm, currentPage],
    async () => {
      const params = {
        page: currentPage,
        page_size: pageSize
      }
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm
      }
      const response = await api.get('/accounting/catalogos-sat/', { params })
      
      // El backend devuelve paginación: { count, next, previous, results }
      return {
        items: response.data.results || response.data || [],
        count: response.data.count || (Array.isArray(response.data) ? response.data.length : 0),
        next: response.data.next || null,
        previous: response.data.previous || null
      }
    },
    { enabled: !!user, keepPreviousData: true }
  )

  const catalog = catalogData?.items || []
  const totalCount = catalogData?.count || 0
  const totalPages = Math.ceil(totalCount / pageSize)

  // Obtener clientes para el selector
  const { data: clients } = useQuery(
    'clients-for-catalog-config',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener configuraciones por cliente con filtro
  const { data: clientConfigs, isLoading: isLoadingConfigs } = useQuery(
    ['sat-client-configs', configFilterClientId],
    async () => {
      const params = {}
      // Filtrar por cliente_id si hay selección
      if (configFilterClientId) {
        params.cliente_id = configFilterClientId
      }
      const response = await api.get('/accounting/configuraciones-catalogo-cliente/', { params })
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Mutación para crear/actualizar configuración
  const saveConfigMutation = useMutation(
    async (configData) => {
      if (editingConfig) {
        // Actualizar configuración existente
        const response = await api.put(
          `/accounting/configuraciones-catalogo-cliente/${editingConfig.id}/`,
          {
            cliente: parseInt(configData.cliente_id),
            version_catalogo: parseInt(configData.version_id),
            fecha_aplicacion: configData.fecha_aplicacion,
            personalizaciones: configData.personalizaciones || {}
          }
        )
        return response.data
      } else {
        // Crear nueva configuración usando el endpoint estándar o actualizar_version
        const response = await api.post(
          '/accounting/configuraciones-catalogo-cliente/actualizar_version/',
          configData
        )
        return response.data
      }
    },
    {
      onSuccess: () => {
        toast.success(editingConfig ? 'Configuración actualizada correctamente' : 'Configuración creada correctamente')
        queryClient.invalidateQueries('sat-client-configs')
        setShowConfigModal(false)
        resetConfigForm()
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 'Error al guardar la configuración'
        toast.error(errorMessage)
      },
      onSettled: () => {
        setIsSavingConfig(false)
      },
    }
  )

  // Mutación para eliminar configuración
  const deleteConfigMutation = useMutation(
    async (configId) => {
      await api.delete(`/accounting/configuraciones-catalogo-cliente/${configId}/`)
    },
    {
      onSuccess: () => {
        toast.success('Configuración eliminada correctamente')
        queryClient.invalidateQueries('sat-client-configs')
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 'Error al eliminar la configuración'
        toast.error(errorMessage)
      },
    }
  )

  const resetConfigForm = () => {
    setEditingConfig(null)
    setConfigClienteId('')
    setConfigVersionId('')
    setConfigFechaAplicacion('')
  }

  const handleOpenConfigModal = (config = null) => {
    if (config) {
      setEditingConfig(config)
      setConfigClienteId(config.cliente?.id || config.cliente || '')
      setConfigVersionId(config.version_catalogo?.id || config.version_catalogo || '')
      setConfigFechaAplicacion(config.fecha_aplicacion || '')
    } else {
      resetConfigForm()
    }
    setShowConfigModal(true)
  }

  const handleSaveConfig = () => {
    if (!configClienteId || !configVersionId || !configFechaAplicacion) {
      toast.error('Por favor completa todos los campos obligatorios')
      return
    }

    setIsSavingConfig(true)
    const configData = {
      cliente_id: parseInt(configClienteId),
      version_id: parseInt(configVersionId),
      fecha_aplicacion: configFechaAplicacion,
      personalizaciones: {} // Por ahora vacío, se puede expandir en el futuro
    }

    saveConfigMutation.mutate(configData)
  }

  const handleDeleteConfig = (config) => {
    if (window.confirm(`¿Estás seguro de eliminar la configuración para ${config.cliente_name || 'este cliente'}?`)) {
      deleteConfigMutation.mutate(config.id)
    }
  }

  // Mutación para crear/actualizar registro del catálogo
  const saveCatalogItemMutation = useMutation(
    async (itemData) => {
      if (editingCatalogItem) {
        const response = await api.put(
          `/accounting/catalogos-sat/${editingCatalogItem.id}/`,
          itemData
        )
        return response.data
      } else {
        const response = await api.post('/accounting/catalogos-sat/', itemData)
        return response.data
      }
    },
    {
      onSuccess: () => {
        toast.success(editingCatalogItem ? 'Registro actualizado correctamente' : 'Registro creado correctamente')
        queryClient.invalidateQueries('sat-catalog')
        setShowCatalogItemModal(false)
        resetCatalogItemForm()
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 
                           (error.response?.data && typeof error.response.data === 'object' 
                             ? JSON.stringify(error.response.data) 
                             : 'Error al guardar el registro')
        toast.error(errorMessage)
      },
      onSettled: () => {
        setIsSavingCatalogItem(false)
      },
    }
  )

  // Mutación para eliminar registro del catálogo
  const deleteCatalogItemMutation = useMutation(
    async (itemId) => {
      await api.delete(`/accounting/catalogos-sat/${itemId}/`)
    },
    {
      onSuccess: () => {
        toast.success('Registro eliminado correctamente')
        queryClient.invalidateQueries('sat-catalog')
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 'Error al eliminar el registro'
        toast.error(errorMessage)
      },
    }
  )

  const resetCatalogItemForm = () => {
    setEditingCatalogItem(null)
    setCatalogItemClave('')
    setCatalogItemDescripcion('')
    setCatalogItemIncluye('')
    setCatalogItemSimilares('')
    setCatalogItemVersionId('')
    setCatalogItemActivo(true)
  }

  const handleOpenCatalogItemModal = (item = null) => {
    if (item) {
      setEditingCatalogItem(item)
      setCatalogItemClave(item.clave || '')
      setCatalogItemDescripcion(item.descripcion || '')
      setCatalogItemIncluye(item.incluye || '')
      setCatalogItemSimilares(item.similares || '')
      setCatalogItemVersionId(item.version?.id || item.version || '')
      setCatalogItemActivo(item.activo !== undefined ? item.activo : true)
    } else {
      resetCatalogItemForm()
      // Si hay versiones disponibles, seleccionar la primera activa por defecto
      if (versions && versions.length > 0) {
        const activeVersion = versions.find(v => v.activo) || versions[0]
        if (activeVersion) {
          setCatalogItemVersionId(activeVersion.id)
        }
      }
    }
    setShowCatalogItemModal(true)
  }

  const handleSaveCatalogItem = () => {
    if (!catalogItemClave || !catalogItemDescripcion || !catalogItemVersionId) {
      toast.error('Por favor completa los campos obligatorios: Código, Descripción y Versión')
      return
    }

    setIsSavingCatalogItem(true)
    const itemData = {
      clave: catalogItemClave.trim(),
      descripcion: catalogItemDescripcion.trim(),
      incluye: catalogItemIncluye.trim() || '',
      similares: catalogItemSimilares.trim() || '',
      version: parseInt(catalogItemVersionId),
      activo: catalogItemActivo
    }

    saveCatalogItemMutation.mutate(itemData)
  }

  const handleDeleteCatalogItem = (item) => {
    if (window.confirm(`¿Estás seguro de eliminar el registro "${item.clave} - ${item.descripcion}"?`)) {
      deleteCatalogItemMutation.mutate(item.id)
    }
  }

  // Mutación para cargar catálogo
  const uploadCatalogMutation = useMutation(
    async (formData) => {
      const response = await api.post('/accounting/versiones-catalogo-sat/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success(`Catálogo cargado correctamente: ${data.loaded_count} elementos nuevos, ${data.skipped_count} duplicados omitidos`)
        queryClient.invalidateQueries('sat-versions')
        queryClient.invalidateQueries('sat-catalog')
        setShowUploadModal(false)
        setUploadFile(null)
        setUploadVersion('4.0')
        setUploadFechaInicio('')
        setUploadFechaFin('')
        setUploadDescripcion('')
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || 'Error al cargar el catálogo'
        toast.error(errorMessage)
      },
      onSettled: () => {
        setIsUploading(false)
      },
    }
  )

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      const name = file.name.toLowerCase()
      if (!name.endsWith('.xml') && !name.endsWith('.csv')) {
        toast.error('El archivo debe ser XML o CSV')
        return
      }
      setUploadFile(file)
    }
  }

  const handleUpload = () => {
    if (!uploadFile) {
      toast.error('Por favor selecciona un archivo XML o CSV')
      return
    }

    if (!uploadVersion || !uploadFechaInicio) {
      toast.error('Por favor completa los campos obligatorios: Versión y Fecha de Inicio')
      return
    }

    const formData = new FormData()
    const isCsv = uploadFile.name.toLowerCase().endsWith('.csv')
    formData.append(isCsv ? 'csv_file' : 'xml_file', uploadFile)
    formData.append('version', uploadVersion)
    formData.append('fecha_inicio', uploadFechaInicio)
    if (uploadFechaFin) {
      formData.append('fecha_fin', uploadFechaFin)
    }
    if (uploadDescripcion) {
      formData.append('descripcion', uploadDescripcion)
    }

    setIsUploading(true)
    uploadCatalogMutation.mutate(formData)
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Catálogos SAT</h3>
        <Button variant="outline-primary" onClick={() => setShowUploadModal(true)}>
          Actualizar Catálogo
        </Button>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="versions" title="Versiones">
          <Card>
            <Card.Body>
              {versions && versions.length > 0 ? (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Versión</th>
                      <th>Fecha Inicio</th>
                      <th>Fecha Fin</th>
                      <th>Estado</th>
                      <th>Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((version) => (
                      <tr key={version.id}>
                        <td>{version.version}</td>
                        <td>{formatDateOnlyEsMX(version.fecha_inicio_vigencia, {}, '—')}</td>
                        <td>{version.fecha_fin_vigencia ? formatDateOnlyEsMX(version.fecha_fin_vigencia, {}, '—') : 'Vigente'}</td>
                        <td>
                          <Badge bg={version.activo ? 'success' : 'secondary'}>
                            {version.activo ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        <td>{version.descripcion || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">No hay versiones de catálogo registradas</Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="catalog" title="Catálogo">
          <Card>
            <Card.Body>
              <div className="mb-3">
                <Row>
                  <Col md={6}>
                    <Form.Control
                      type="text"
                      placeholder="Buscar por código o descripción..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col md={6} className="d-flex align-items-center justify-content-end gap-2">
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => handleOpenCatalogItemModal()}
                    >
                      + Agregar Registro
                    </Button>
                    <Form.Text className="text-muted">
                      {isLoadingCatalog 
                        ? 'Buscando...' 
                        : totalCount > 0 
                          ? `${totalCount} elemento(s) total(es) - Página ${currentPage} de ${totalPages}`
                          : debouncedSearchTerm 
                            ? 'No se encontraron resultados' 
                            : 'Ingresa un término de búsqueda o explora los elementos cargados'}
                    </Form.Text>
                  </Col>
                </Row>
              </div>
              {isLoadingCatalog ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Cargando catálogo...</p>
                </div>
              ) : catalog && catalog.length > 0 ? (
                <>
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table responsive striped hover>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                        <tr>
                          <th>Código</th>
                          <th>Descripción</th>
                          <th>Versión</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catalog.map((item) => (
                          <tr key={item.id}>
                            <td><strong>{item.clave}</strong></td>
                            <td>{item.descripcion || 'N/A'}</td>
                            <td>{item.version_display || item.version?.version || 'N/A'}</td>
                            <td>
                              <Badge bg={item.activo ? 'success' : 'secondary'}>
                                {item.activo ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleOpenCatalogItemModal(item)}
                                style={{ minWidth: '80px' }}
                              >
                                <i className="bi bi-pencil me-1"></i>
                                <span>Editar</span>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteCatalogItem(item)}
                                style={{ minWidth: '80px' }}
                              >
                                <i className="bi bi-trash me-1"></i>
                                <span>Eliminar</span>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  
                  {/* Paginación */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <Pagination.First 
                          onClick={() => setCurrentPage(1)} 
                          disabled={currentPage === 1 || isLoadingCatalog}
                        />
                        <Pagination.Prev 
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                          disabled={currentPage === 1 || isLoadingCatalog}
                        />
                        
                        {/* Mostrar números de página */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum
                          if (totalPages <= 5) {
                            pageNum = i + 1
                          } else if (currentPage <= 3) {
                            pageNum = i + 1
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i
                          } else {
                            pageNum = currentPage - 2 + i
                          }
                          
                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === currentPage}
                              onClick={() => setCurrentPage(pageNum)}
                              disabled={isLoadingCatalog}
                            >
                              {pageNum}
                            </Pagination.Item>
                          )
                        })}
                        
                        <Pagination.Next 
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                          disabled={currentPage === totalPages || isLoadingCatalog}
                        />
                        <Pagination.Last 
                          onClick={() => setCurrentPage(totalPages)} 
                          disabled={currentPage === totalPages || isLoadingCatalog}
                        />
                      </Pagination>
                    </div>
                  )}
                  
                  <div className="mt-2 text-center">
                    <small className="text-muted">
                      Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} de {totalCount} elementos
                    </small>
                  </div>
                </>
              ) : (
                <Alert variant="info">
                  {debouncedSearchTerm 
                    ? `No se encontraron elementos que coincidan con "${debouncedSearchTerm}"`
                    : 'No hay elementos en el catálogo. Carga un catálogo desde el botón "Actualizar Catálogo".'}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="configs" title="Configuraciones por Cliente">
          <Card>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Filtrar por Cliente</Form.Label>
                    <Form.Select
                      value={configFilterClientId}
                      onChange={(e) => setConfigFilterClientId(e.target.value)}
                      disabled={selectDisabled}
                    >
                      <option value="">Todos los clientes</option>
                      {clients && clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.razon_social || client.business_name || `${client.nombre || ''} ${client.apellido_paterno || ''}`.trim() || client.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Button variant="primary" onClick={() => handleOpenConfigModal()}>
                    + Nueva Configuración
                  </Button>
                </Col>
              </Row>

              {isLoadingConfigs ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Cargando configuraciones...</p>
                </div>
              ) : clientConfigs && clientConfigs.length > 0 ? (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Versión Catálogo</th>
                      <th>Fecha de Aplicación</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientConfigs.map((config) => (
                      <tr key={config.id}>
                        <td><strong>{config.cliente_name || 'N/A'}</strong></td>
                        <td>
                          <Badge bg="info">
                            {config.version_catalogo_display || config.version_catalogo?.version || 'N/A'}
                          </Badge>
                        </td>
                        <td>{formatDateOnlyEsMX(config.fecha_aplicacion, {}, '—')}</td>
                        <td>
                          <Badge bg={config.activo ? 'success' : 'secondary'}>
                            {config.activo ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleOpenConfigModal(config)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteConfig(config)}
                          >
                            Eliminar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">
                  {configFilterClientId 
                    ? `No hay configuraciones para el cliente seleccionado.`
                    : 'No hay configuraciones por cliente. Crea una nueva configuración para asignar una versión específica del catálogo a un cliente.'}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Modal para cargar catálogo */}
      <Modal show={showUploadModal} onHide={() => !isUploading && setShowUploadModal(false)} size="lg">
        <Modal.Header closeButton={!isUploading}>
          <Modal.Title>Cargar Catálogo SAT</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            Selecciona un archivo XML o CSV del catálogo SAT (c_ClaveProdServ) y proporciona la información de la versión.
          </Alert>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Archivo (XML o CSV) *</Form.Label>
              <Form.Control
                type="file"
                accept=".xml,.csv,application/xml,text/csv,text/plain"
                onChange={handleFileSelect}
                disabled={isUploading}
              />
              <Form.Text className="text-muted">
                {uploadFile
                  ? `Archivo seleccionado: ${uploadFile.name}`
                  : 'Formatos: .xml (SAT) o .csv (ej. c_ClaveProdServ). Si no ves CSV, usa "Todos los archivos" en el selector.'}
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Versión *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: 4.0"
                    value={uploadVersion}
                    onChange={(e) => setUploadVersion(e.target.value)}
                    disabled={isUploading}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de Inicio de Vigencia *</Form.Label>
                  <Form.Control
                    type="date"
                    value={uploadFechaInicio}
                    onChange={(e) => setUploadFechaInicio(e.target.value)}
                    disabled={isUploading}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de Fin de Vigencia</Form.Label>
                  <Form.Control
                    type="date"
                    value={uploadFechaFin}
                    onChange={(e) => setUploadFechaFin(e.target.value)}
                    disabled={isUploading}
                    placeholder="Opcional"
                  />
                  <Form.Text className="text-muted">
                    Deja en blanco si la versión sigue vigente
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: Catálogo SAT 2024 - CFDI 4.0"
                    value={uploadDescripcion}
                    onChange={(e) => setUploadDescripcion(e.target.value)}
                    disabled={isUploading}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowUploadModal(false)}
            disabled={isUploading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={isUploading || !uploadFile || !uploadVersion || !uploadFechaInicio}
          >
            {isUploading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Cargando...
              </>
            ) : (
              'Cargar Catálogo'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para crear/editar configuración de catálogo por cliente */}
      <Modal show={showConfigModal} onHide={() => !isSavingConfig && setShowConfigModal(false)} size="lg">
        <Modal.Header closeButton={!isSavingConfig}>
          <Modal.Title>
            {editingConfig ? 'Editar Configuración de Catálogo' : 'Nueva Configuración de Catálogo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <strong>¿Cuándo usar esta configuración?</strong>
            <ul className="mb-0 mt-2">
              <li>Migración gradual entre versiones de catálogo (ej: CFDI 3.3 → 4.0)</li>
              <li>Validación de facturas históricas con versiones antiguas</li>
              <li>Clientes con períodos fiscales especiales</li>
              <li>Transiciones controladas entre versiones</li>
            </ul>
          </Alert>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Cliente *</Form.Label>
              <Form.Select
                value={configClienteId}
                onChange={(e) => setConfigClienteId(e.target.value)}
                disabled={isSavingConfig || !!editingConfig}
              >
                <option value="">Seleccionar cliente...</option>
                {clients && clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.razon_social || client.business_name || `${client.nombre || ''} ${client.apellido_paterno || ''}`.trim() || client.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {editingConfig ? 'No se puede cambiar el cliente de una configuración existente' : 'Selecciona el cliente al que se asignará esta configuración'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Versión del Catálogo *</Form.Label>
              <Form.Select
                value={configVersionId}
                onChange={(e) => setConfigVersionId(e.target.value)}
                disabled={isSavingConfig}
              >
                <option value="">Selecciona una versión</option>
                {versions && versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.version} - {version.descripcion || 'Sin descripción'} 
                    {version.fecha_inicio_vigencia && ` (Desde ${formatDateOnlyEsMX(version.fecha_inicio_vigencia, {}, '—')})`}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Versión del catálogo SAT que se usará para este cliente
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Fecha de Aplicación *</Form.Label>
              <Form.Control
                type="date"
                value={configFechaAplicacion}
                onChange={(e) => setConfigFechaAplicacion(e.target.value)}
                disabled={isSavingConfig}
              />
              <Form.Text className="text-muted">
                Fecha desde la cual se aplicará esta configuración. Esta fecha debe ser mayor o igual a la fecha de inicio de vigencia de la versión seleccionada.
              </Form.Text>
            </Form.Group>

            {editingConfig && (
              <Alert variant="warning" className="mt-3">
                <strong>Nota:</strong> Al guardar, se desactivará automáticamente cualquier otra configuración activa para este cliente.
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfigModal(false)}
            disabled={isSavingConfig}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveConfig}
            disabled={isSavingConfig || !configClienteId || !configVersionId || !configFechaAplicacion}
          >
            {isSavingConfig ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Guardando...
              </>
            ) : (
              editingConfig ? 'Actualizar' : 'Crear'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para crear/editar registro del catálogo */}
      <Modal show={showCatalogItemModal} onHide={() => !isSavingCatalogItem && setShowCatalogItemModal(false)} size="lg">
        <Modal.Header closeButton={!isSavingCatalogItem}>
          <Modal.Title>
            {editingCatalogItem ? 'Editar Registro del Catálogo' : 'Agregar Registro al Catálogo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <strong>Nota:</strong> Los registros agregados manualmente estarán disponibles para todos los clientes del sistema.
          </Alert>

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Código (Clave) *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: 841115"
                    value={catalogItemClave}
                    onChange={(e) => setCatalogItemClave(e.target.value)}
                    disabled={isSavingCatalogItem}
                    maxLength={20}
                  />
                  <Form.Text className="text-muted">
                    Código del producto o servicio según el catálogo SAT (máximo 20 caracteres)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Versión del Catálogo *</Form.Label>
                  <Form.Select
                    value={catalogItemVersionId}
                    onChange={(e) => setCatalogItemVersionId(e.target.value)}
                    disabled={isSavingCatalogItem}
                  >
                    <option value="">Selecciona una versión</option>
                    {versions && versions.map((version) => (
                      <option key={version.id} value={version.id}>
                        {version.version} - {version.descripcion || 'Sin descripción'}
                        {version.fecha_inicio_vigencia && ` (Desde ${formatDateOnlyEsMX(version.fecha_inicio_vigencia, {}, '—')})`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Versión del catálogo SAT a la que pertenece este registro
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Descripción *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: Servicios de facturación electrónica"
                value={catalogItemDescripcion}
                onChange={(e) => setCatalogItemDescripcion(e.target.value)}
                disabled={isSavingCatalogItem}
                as="textarea"
                rows={3}
              />
              <Form.Text className="text-muted">
                Descripción detallada del producto o servicio
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Incluye</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: Servicios de timbrado, generación de XML, validación..."
                value={catalogItemIncluye}
                onChange={(e) => setCatalogItemIncluye(e.target.value)}
                disabled={isSavingCatalogItem}
                as="textarea"
                rows={2}
              />
              <Form.Text className="text-muted">
                Opcional: Especifica qué incluye este producto o servicio
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Similares</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: Servicios relacionados o alternativos"
                value={catalogItemSimilares}
                onChange={(e) => setCatalogItemSimilares(e.target.value)}
                disabled={isSavingCatalogItem}
                as="textarea"
                rows={2}
              />
              <Form.Text className="text-muted">
                Opcional: Productos o servicios similares o relacionados
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="catalog-item-activo"
                label="Activo"
                checked={catalogItemActivo}
                onChange={(e) => setCatalogItemActivo(e.target.checked)}
                disabled={isSavingCatalogItem}
              />
              <Form.Text className="text-muted">
                Los registros inactivos no estarán disponibles para selección en facturas
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCatalogItemModal(false)}
            disabled={isSavingCatalogItem}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveCatalogItem}
            disabled={isSavingCatalogItem || !catalogItemClave || !catalogItemDescripcion || !catalogItemVersionId}
          >
            {isSavingCatalogItem ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Guardando...
              </>
            ) : (
              editingCatalogItem ? 'Actualizar' : 'Crear'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default SATCatalogs

